import { TextOrHTML } from "@simplybusiness/mobius";
import classNames from "classnames/dedupe";
import { useEffect, useMemo, useState } from "react";
import type { FlattenedSectionProps } from ".";
import {
  useAnswers,
  useCurrentSection,
  useQuestionnaire,
} from "../../../features/questionnaire";
import { useLocale } from "../../../hooks/useLocale";
import { useMutationStatus } from "../../../hooks/useMutationStatus";
import { SubmitAction } from "../Actions";
import { Collapsible } from "../Collapsible";
import { groupByAssumption } from "./groupByAssumption";
import { isQuestion } from "./Question/types";

export const ReviewSection = ({ ref, ...props }: FlattenedSectionProps) => {
  const {
    title,
    preamble,
    postamble = "By continuing, you agree that the above statements are up to date and correct to the best of your knowledge.",
    questions,
    className,
    submitActionLabel = "Continue to quotes",
  } = props;
  const classes = classNames("mobius", "mobius-section", className);
  const {
    sections: sectionsFromState,
    showQuestion,
    editQuestion,
    selectedQuestion,
    changedQuestions,
    changedAssumptionGroupIndex,
    confirmedAssumptionGroups,
    confirmAssumptionGroup,
    setEditMode,
    addressModes,
    errors,
    groups,
  } = useQuestionnaire();
  const { currentSectionIndex } = useCurrentSection();
  const answers = useAnswers();
  // By the time we review, the sections have been flattened
  const sections = sectionsFromState as unknown as FlattenedSectionProps[];
  const isMutating = useMutationStatus("prepopulateAnswers");
  const [allowSubmission, setAllowSubmission] = useState(false);
  const [expandedAssumptionGroup, setExpandedAssumptionGroup] = useState(
    changedAssumptionGroupIndex,
  );
  const { locale } = useLocale();

  useEffect(() => {
    // If a question has been edited, scroll it to the top
    const questionName = changedQuestions?.[0] || selectedQuestion;

    if (questionName) {
      const element = document.getElementById(questionName);

      if (element) {
        element.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    }
    setEditMode(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const assumptionGroupsData = useMemo(
    () =>
      groupByAssumption({
        questions: questions.filter(isQuestion), // filter out groups to prevent runtime errors on the first render before flattening.
        answers,
        sections,
        addressModes,
        locale,
        groups,
      }),
    [questions, answers, sections, addressModes, locale, groups],
  );

  const handleEditQuestion = (questionName: string) => {
    const foundIndex = sections.findIndex(({ questions }) =>
      questions.some(({ name }) => name === questionName),
    );

    if (foundIndex < currentSectionIndex) {
      showQuestion(questionName);
    } else {
      editQuestion({
        questionName,
        assumptionGroupIndex: expandedAssumptionGroup,
      });
    }
  };

  return (
    <section data-testid="mobius-review-section" ref={ref} className={classes}>
      {title && <h2 className="mobius-section__title">{title}</h2>}
      {preamble && (
        <TextOrHTML
          className="mobius-section__preamble"
          text={preamble}
          textWrapper
        />
      )}
      <Collapsible
        assumptionGroups={assumptionGroupsData}
        expandedAssumptionGroup={expandedAssumptionGroup}
        confirmedAssumptionGroups={confirmedAssumptionGroups}
        onAllConfirmed={() => setAllowSubmission(true)}
        onEditQuestion={handleEditQuestion}
        onAssumptionGroupExpanded={setExpandedAssumptionGroup}
        onConfirmAssumptionGroup={confirmAssumptionGroup}
      />

      <SubmitAction
        isDisabled={isMutating || !allowSubmission}
        buttonLabel={submitActionLabel}
        errors={errors}
      >
        <TextOrHTML
          className="mobius-section__postamble"
          text={postamble}
          textWrapper
        />
      </SubmitAction>
    </section>
  );
};

ReviewSection.displayName = "ReviewSection";
